import Notifications from 'react-notification-system-redux';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as api from '../../utilities/ServiceManager';
import {
  setActivateExamSuccess,
  setActivateExamWaiting,
  setBuildAllExamsSuccess,
  setBuildAllExamsWaiting,
  setBuildPracticeExamWaiting,
  setExamAfterBuild,
  setPracticeExamInformation,
  setPracticeExamInformationWaiting,
  setRemoveQuestionSuccess,
  setRemoveQuestionWaiting,
} from './actions';
import {
  ACTIVATE_EXAM,
  BUILD_ALL_EXAMS,
  BUILD_PRACTICE_EXAM,
  CHECK_BUILD_PE_PROGRESS,
  CREATE_PRACTICE_EXAM,
  LOAD_PRACTICE_EXAM_INFORMATION,
  REMOVE_QUESTION,
} from './actionTypesSagas';
import { getDutyState, getRank, getRate } from './selectors';

export function* watchLoadPracticeExamInformation() {
  yield takeLatest(
    LOAD_PRACTICE_EXAM_INFORMATION,
    loadPracticeExamInformationSaga,
  );
}

function* loadPracticeExamInformationSaga(action) {
  try {
    yield put(setPracticeExamInformationWaiting(true));

    const response = yield call(
      api.loadWeeklyPracticeExamsData,
      action.rate,
      action.rank,
      action.dutyState,
      action.practiceExamId,
    );

    if (response.error) {
      yield put(
        Notifications.show(
          {
            title: 'Error!',
            message: `No exams related`,
          },
          'error',
        ),
      );
      yield put(setPracticeExamInformationWaiting(false));
      yield put(setPracticeExamInformation([]));
    } else {
      yield put(setPracticeExamInformation(response));
    }

    yield put(setPracticeExamInformationWaiting(false));
  } catch (err) {
    yield put(
      Notifications.show(
        { title: 'Error!', message: err.response.data },
        'error',
      ),
    );
    yield put(setPracticeExamInformationWaiting(false));
    yield put(setPracticeExamInformation([]));
  }
}

export function* watchCreatePracticeExam() {
  yield takeLatest(CREATE_PRACTICE_EXAM, createPracticeExamSaga);
}

function* createPracticeExamSaga(action) {
  try {
    yield put(setBuildPracticeExamWaiting(true));

    const response = yield call(api.createWeeklyPracticeExam, action.examId);

    if (response.alreadyRunning) {
      yield put(
        Notifications.show(
          {
            title: 'Error!',
            message:
              'A practice exam build is already running. Try again in a few minutes',
          },
          'warning',
        ),
      );
    } else if (response.alreadyHasCurrentWeek) {
      yield put(
        Notifications.show(
          {
            title: 'Error!',
            message: 'This exam already has a practice exam for this week',
          },
          'warning',
        ),
      );
    } else if (response.created) {
      yield put(
        Notifications.show(
          {
            title: 'Success!',
            message: 'Practice exam created',
            autoDismiss: 1,
          },
          'success',
        ),
      );
    }

    yield put(setBuildPracticeExamWaiting(false));

    // Reload so the new exam shows up in the selector.
    const rate = yield select(getRate);
    const rank = yield select(getRank);
    const dutyState = yield select(getDutyState);

    yield call(loadPracticeExamInformationSaga, { rate, rank, dutyState });
  } catch (err) {
    yield put(
      Notifications.show(
        { title: 'Error!', message: err.response.data },
        'error',
      ),
    );
    yield put(setBuildPracticeExamWaiting(false));
  }
}

export function* watchBuildPracticeExam() {
  yield takeLatest(BUILD_PRACTICE_EXAM, buildPracticeExamSaga);
}

function* buildPracticeExamSaga(action) {
  try {
    yield put(setBuildPracticeExamWaiting(true));

    const response = yield call(
      api.rebuildWeeklyPracticeExam,
      action.practiceExamId,
    );

    if (response.error) {
      yield put(
        Notifications.show(
          { title: 'Error!', message: response.error },
          'error',
        ),
      );
    } else {
      yield put(setExamAfterBuild(response.practiceExam, response.questions));
    }

    yield put(setBuildPracticeExamWaiting(false));
  } catch (err) {
    yield put(
      Notifications.show(
        { title: 'Error!', message: err.response.data },
        'error',
      ),
    );
    yield put(setBuildPracticeExamWaiting(false));
  }
}

export function* watchRemoveQuestion() {
  yield takeLatest(REMOVE_QUESTION, removeQuestionSaga);
}

function* removeQuestionSaga(action) {
  try {
    yield put(setRemoveQuestionWaiting(true));

    const response = yield call(
      api.removeWeeklyPracticeExamQuestion,
      action.practiceExamId,
      action.questionId,
    );

    if (response.error) {
      yield put(setRemoveQuestionSuccess(false));
      yield put(
        Notifications.show(
          { title: 'Error!', message: response.error },
          'error',
        ),
      );
    } else {
      yield put(
        setExamAfterBuild(
          response.practiceExam,
          response.questions,
          response.newQuestionId,
        ),
      );
      yield put(setRemoveQuestionSuccess(true));
    }

    yield put(setRemoveQuestionWaiting(false));
  } catch (err) {
    yield put(
      Notifications.show(
        { title: 'Error!', message: err.response.data },
        'error',
      ),
    );
    yield put(setRemoveQuestionWaiting(false));
    yield put(setRemoveQuestionSuccess(false));
  }
}

export function* watchActivateExam() {
  yield takeLatest(ACTIVATE_EXAM, activateExamSaga);
}

function* activateExamSaga(action) {
  try {
    yield put(setActivateExamWaiting(true));

    const response = yield call(
      api.activatePracticeExam,
      action.practiceExamId,
    );

    if (response.error) {
      yield put(
        Notifications.show(
          { title: 'Error!', message: response.error },
          'error',
        ),
      );
      yield put(setActivateExamSuccess(false));
    } else {
      yield put(
        Notifications.show(
          {
            title: 'Success!',
            message: 'Practice exam activated',
            autoDismiss: 1,
          },
          'success',
        ),
      );
      yield put(setExamAfterBuild(response, response.questions));
      yield put(setActivateExamSuccess(true));
    }

    yield put(setActivateExamWaiting(false));
  } catch (err) {
    yield put(
      Notifications.show(
        { title: 'Error!', message: err.response.data },
        'error',
      ),
    );
    yield put(setActivateExamWaiting(false));
    yield put(setActivateExamSuccess(false));
  }
}

export function* watchCheckBuilPEProgress() {
  yield takeLatest(CHECK_BUILD_PE_PROGRESS, checkBuildPEProgressSagas);
}

export function* checkBuildPEProgressSagas() {
  try {
    const isRunning = yield call(api.checkBuildPracticeExamsProgress);
    yield put(setBuildAllExamsWaiting(isRunning));
  } catch (err) {
    yield put(setBuildAllExamsWaiting(false));
  }
}

export function* watchBuildAllExams() {
  yield takeLatest(BUILD_ALL_EXAMS, buildAllExamsSaga);
}

function* buildAllExamsSaga(action) {
  try {
    const isRunning = yield call(api.checkBuildPracticeExamsProgress);
    if (!isRunning) {
      const { ratesList } = action.payload;
      yield call(api.generateWeeklyPracticeExams, ratesList);
    }
    yield put(setBuildAllExamsWaiting(true));
  } catch (err) {
    yield put(setBuildAllExamsWaiting(false));
    yield put(setBuildAllExamsSuccess(false));
    yield put(
      Notifications.show(
        {
          title: 'Cannot activate exam!',
          message: err.response.data,
        },
        'error',
      ),
    );
  }
}

/* SAGAS EXPORT */
export default function* sagas() {
  yield all([
    watchLoadPracticeExamInformation(),
    watchCreatePracticeExam(),
    watchBuildPracticeExam(),
    watchRemoveQuestion(),
    watchActivateExam(),
    watchBuildAllExams(),
    watchCheckBuilPEProgress(),
  ]);
}
